import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  ShieldCheck,
  FileEdit,
  Globe,
  BrainCircuit,
  CalendarClock,
  Video,
  Code2,
  BarChart3,
  CheckCircle2,
  Clock,
  Sparkles,
  Mic2,
  Mic,
  PhoneCall,
  MessageCircle,
} from 'lucide-react';
import { Button } from '../../../Components/ui/button';

/**
 * New-generation marketing hero for SearchJob.ai.
 *
 * Centerpiece: a LIVE AGENT RUN in the product window, shown as a
 * SPOTLIGHT that travels through the agents — one agent is in focus
 * at a time. A flow trail of nodes across the top fills and hands off
 * as each agent completes, then the next agent's identity + live
 * screen animate in. This reads like watching a guided demo, not a
 * static dashboard.
 *
 * Agent run order (9 stages, the real funnel):
 *   1. JD Agent          — voice → job description
 *   2. Auto-Sourcing Agent— AI finds & invites matches + inbound
 *   3. Screening Agent   — AI resume scoring & ranking
 *   4. Phone Screen Agent— AI voice-call HR screen (English)
 *   5. WhatsApp Agent    — chat screening + status updates
 *   6. Scheduler Agent   — auto-booking interviews
 *   7. Interview Agent   — realtime AI video call
 *   8. Coding Agent      — proctored test auto-grading
 *   9. Analytics Agent   — funnel report & final shortlist
 *
 * Each stage runs WORK_MS, then the spotlight advances; on the last
 * stage it holds HOLD_MS and loops. Trail nodes are clickable to jump.
 * The funnel numbers (142 → 94 → 28 → 8 → 3 booked) match the workflow
 * section and product mockups — one consistent story.
 *
 * The run loops continuously (no hover pause); `prefers-reduced-motion` renders a static
 * mid-run snapshot (no timers). The window holds a fixed 4° tilt.
 */

const WORK_MS = 1000; // how long each agent "works" on its screen
const HOLD_MS = 1500; // how long the completed run holds before looping

// Rotating "New" announcement pill — cycles through recent launches.
const ANNOUNCEMENTS = [
  'AI auto-sourcing — candidates found & invited for you',
  'Realtime AI video interviews now in multiple languages',
];

export default function HiringHero() {
  const navigate = useNavigate();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  // Rotate the announcement pill every 4s (paused under reduced motion).
  const [annIdx, setAnnIdx] = useState(0);
  useEffect(() => {
    const reduced = window.matchMedia?.(
      '(prefers-reduced-motion: reduce)'
    )?.matches;
    if (reduced) return;
    const id = setInterval(
      () => setAnnIdx((i) => (i + 1) % ANNOUNCEMENTS.length),
      4000
    );
    return () => clearInterval(id);
  }, []);

  return (
    <section className="relative overflow-hidden bg-background isolate">
      {/* ─── Gradient mesh background ─── */}
      <div className="absolute inset-0 -z-10 pointer-events-none">
        <div className="absolute top-[-15%] left-[8%] w-[55%] h-[55%] bg-primary/15 rounded-full blur-3xl animate-mesh-1" />
        <div className="absolute top-[5%] right-[5%] w-[45%] h-[45%] bg-emerald-500/10 rounded-full blur-3xl animate-mesh-2" />
        <div className="absolute bottom-[-25%] left-1/2 -translate-x-1/2 w-[70%] h-[50%] bg-teal-500/10 rounded-full blur-3xl" />
      </div>

      {/* ─── Subtle grid overlay ─── */}
      <div
        className="absolute inset-0 -z-10 opacity-[0.14] pointer-events-none"
        style={{
          backgroundImage:
            'linear-gradient(to right, currentColor 1px, transparent 1px), linear-gradient(to bottom, currentColor 1px, transparent 1px)',
          backgroundSize: '44px 44px',
          color: 'hsl(var(--foreground) / 0.1)',
          maskImage:
            'radial-gradient(ellipse 80% 60% at 50% 0%, black 30%, transparent 80%)',
          WebkitMaskImage:
            'radial-gradient(ellipse 80% 60% at 50% 0%, black 30%, transparent 80%)',
        }}
      />

      <div className="relative w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 lg:pt-20 pb-20">
        {/* ═════════════ SPLIT HERO — content left, product image right ═════════════ */}
        <div className="grid lg:grid-cols-[1fr_1.3fr] gap-12 lg:gap-8 xl:gap-12 items-center">
          {/* ── LEFT: announcement, headline, CTAs, trust ── */}
          <div
            className={`text-center lg:text-left transition-all duration-700 ${
              mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
          >
            {/* Announcement pill */}
            {/* <button
              type="button"
              onClick={() =>
                document
                  .getElementById('product-tour')
                  ?.scrollIntoView({ behavior: 'smooth' })
              }
              className="group/pill inline-flex items-center gap-2 pl-1.5 pr-3 py-1.5 rounded-full border border-border bg-card/70 backdrop-blur-md shadow-sm mb-6 hover:border-primary/40 hover:shadow-md transition-all"
            >
              <span className="flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-primary text-primary-foreground text-[10px] font-bold uppercase tracking-wider shrink-0">
                <span className="relative flex w-1.5 h-1.5">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-primary-foreground opacity-60 animate-ping" />
                  <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-primary-foreground" />
                </span>
                New
              </span>
              <span
                key={annIdx}
                className="text-sm text-foreground/80 animate-feed-in text-left"
              >
                {ANNOUNCEMENTS[annIdx]}
              </span>
              <ArrowRight className="w-3.5 h-3.5 text-muted-foreground group-hover/pill:text-primary group-hover/pill:translate-x-0.5 transition-all shrink-0" />
            </button> */}

            {/* Display headline */}
            <h1 className="text-4xl sm:text-5xl lg:text-[3.25rem] xl:text-6xl font-bold tracking-[-0.03em] leading-[1.05] text-foreground mb-5">
              Recruitment, Reimagined
              <br />
              <span className="bg-gradient-to-r from-primary via-emerald-600 to-teal-600 bg-clip-text text-transparent">
                by Agentic AI.
              </span>
            </h1>

            {/* Subheadline */}
            <p className="text-lg sm:text-xl text-muted-foreground leading-relaxed mb-8 max-w-xl mx-auto lg:mx-0">
              AI agents that think, act, and collaborate to streamline sourcing, screening, candidate engagement, and hiring operations at enterprise scale with Human in the loop in every step !
            </p>

            {/* Pill CTAs */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center lg:justify-start mb-5">
              <Button
                size="lg"
                onClick={() => navigate('/hirewithus/demo')}
                className="!bg-primary !text-primary-foreground hover:!bg-primary/90 h-12 px-8 text-base font-semibold group !rounded-full shadow-lg shadow-primary/25 hover:shadow-xl hover:shadow-primary/30 transition-shadow"
              >
                Try SearchJob
                <ArrowRight className="w-4 h-4 ml-1.5 group-hover:translate-x-0.5 transition-transform" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={() => navigate('/hirewithus/book-demo')}
                className="h-12 px-8 text-base font-semibold group !rounded-full bg-card/70 backdrop-blur-md"
              >
                <CalendarClock className="w-4 h-4 mr-1.5" />
                Book a Demo
              </Button>
            </div>

            {/* Micro-reassurance */}
            {/* <p className="text-xs text-muted-foreground mb-6">
              No credit card required · Set up in 30 minutes
            </p> */}

            {/* Trust bar */}
            {/* <div className="flex items-center justify-center lg:justify-start gap-1.5 text-sm text-muted-foreground">
              <ShieldCheck className="w-4 h-4 text-primary shrink-0" />
              <span>Encrypted at rest · India-region data residency</span>
            </div> */}
          </div>

          {/* ── RIGHT: product dashboard image (enlarged, bleeds right) ── */}
          <div
            className={`relative lg:-mr-10 xl:-mr-20 2xl:-mr-28 transition-all duration-1000 delay-150 ${
              mounted
                ? 'opacity-100 translate-y-0 lg:translate-x-0'
                : 'opacity-0 translate-y-8 lg:translate-x-6'
            }`}
          >
            {/* Dashboard with the outer white background + shadow halos
                knocked out (card bodies kept solid). A real drop-shadow adds
                depth, so it floats on the mint page with no white box. */}
            <img
              src="/hero-cutout.png"
              alt="SearchJob.ai recruiter dashboard — AI candidate ranking, resume screening, hiring funnel and interview pipeline"
              width={1536}
              height={1024}
              className="hero-float w-full h-auto"
              style={{ filter: 'drop-shadow(0 14px 26px rgba(18,46,38,0.16))' }}
            />
          </div>
        </div>

        {/* ═════════════ LIVE AGENT RUN — the process, animated ═════════════ */}
        <div className="mt-24 lg:mt-28">
          <div className="text-center max-w-2xl mx-auto mb-8">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-primary/20 bg-primary/5 text-primary text-xs font-semibold uppercase tracking-wider mb-3">
              <Sparkles className="w-3.5 h-3.5" />
              Live agent run
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-foreground">
              Watch the agents work — one role, end to end.
            </h2>
          </div>

          <div
            className={`relative max-w-5xl mx-auto transition-all duration-1000 delay-200 ${
              mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
          >
            {/* Glow bed behind the window */}
            <div className="absolute -inset-x-8 top-8 bottom-0 bg-gradient-to-r from-primary/20 via-emerald-500/15 to-teal-500/20 blur-[90px] rounded-full -z-10" />

            <div className="hero-shot">
              <AgentRun />
            </div>
          </div>
        </div>

        {/* ═════════════ STATS STRIP (count-up) ═════════════ */}
        <div className="mt-20 lg:mt-24 pt-10 border-t border-border">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
            <StatBlock
              to={75}
              suffix="%"
              label="Reduction in time-to-hire"
              sub="Versus manual resume screening"
            />
            <StatBlock
              to={10}
              suffix="×"
              label="Higher screening throughput"
              sub="Without expanding the recruitment team"
            />
            <StatBlock
              to={98}
              suffix="%"
              label="Cheating-detection accuracy"
              sub="Across coding and online assessments"
            />
          </div>
        </div>
      </div>

      {/* ═════════════ KEYFRAMES + TILT ═════════════ */}
      <style>{`
        @keyframes mesh-1 {
          0%, 100% { transform: translate(0, 0) rotate(0deg) scale(1); }
          50% { transform: translate(40px, -30px) rotate(15deg) scale(1.1); }
        }
        @keyframes mesh-2 {
          0%, 100% { transform: translate(0, 0) rotate(0deg) scale(1); }
          50% { transform: translate(-40px, 30px) rotate(-15deg) scale(1.15); }
        }
        .animate-mesh-1 { animation: mesh-1 22s ease-in-out infinite; }
        .animate-mesh-2 { animation: mesh-2 26s ease-in-out infinite; }

        /* Fixed, gentle 4° tilt — no hover movement. */
        .hero-shot {
          transform: perspective(1400px) rotateX(4deg);
          transform-origin: top center;
        }
        @media (prefers-reduced-motion: reduce) {
          .hero-shot { transform: none; }
        }

        /* Gentle float for the hero product image */
        @keyframes hero-float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        .hero-float { animation: hero-float 6s ease-in-out infinite; }
        @media (prefers-reduced-motion: reduce) {
          .hero-float { animation: none; }
        }

        @keyframes scene-in {
          from { opacity: 0; transform: scale(0.985) translateY(6px); }
          to   { opacity: 1; transform: scale(1) translateY(0); }
        }
        .animate-scene-in { animation: scene-in 0.45s ease-out; }

        /* Spotlight entrances — the active agent's identity + screen
           slide/scale in each time the run advances to a new agent. */
        @keyframes agent-rise {
          from { opacity: 0; transform: translateY(10px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .animate-agent-rise {
          animation: agent-rise 0.45s cubic-bezier(0.22, 1, 0.36, 1) both;
        }
        @keyframes screen-rise {
          from { opacity: 0; transform: translateY(16px) scale(0.98); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        .animate-screen-rise {
          animation: screen-rise 0.5s cubic-bezier(0.22, 1, 0.36, 1) 0.06s both;
        }

        @keyframes feed-in {
          from { opacity: 0; transform: translateY(-6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .animate-feed-in { animation: feed-in 0.4s ease-out; }

        @keyframes row-in {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .animate-row-in { animation: row-in 0.45s ease-out both; }

        @keyframes typing-bounce {
          0%, 80%, 100% { transform: translateY(0); opacity: 0.45; }
          40% { transform: translateY(-3px); opacity: 1; }
        }
        .typing-dot {
          width: 4px; height: 4px; border-radius: 9999px;
          background: hsl(var(--primary));
          animation: typing-bounce 1.1s ease-in-out infinite;
        }
        /* WhatsApp typing dots — white, for the dark chat bubble */
        .wa-dot {
          width: 5px; height: 5px; border-radius: 9999px;
          background: rgba(255,255,255,0.65);
          animation: typing-bounce 1.1s ease-in-out infinite;
        }

        @keyframes score-fill {
          0% { width: 0%; }
          100% { width: var(--score-w, 87%); }
        }
        .animate-score-fill { animation: score-fill 1.2s ease-out 0.2s both; }

        @media (prefers-reduced-motion: reduce) {
          .animate-scene-in, .animate-feed-in, .animate-row-in,
          .animate-score-fill, .animate-agent-rise,
          .animate-screen-rise { animation: none; }
          .typing-dot, .wa-dot { animation: none; }
        }
      `}</style>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────── */
/*  StatBlock + CountUp                                             */
/* ─────────────────────────────────────────────────────────────── */
function StatBlock({ to, suffix, label, sub }) {
  return (
    <div className="text-center">
      <div className="text-3xl sm:text-4xl font-bold text-foreground tabular-nums">
        <CountUp to={to} suffix={suffix} />
      </div>
      <div className="mt-1 text-sm font-semibold text-foreground">{label}</div>
      <div className="mt-0.5 text-xs text-muted-foreground">{sub}</div>
    </div>
  );
}

function CountUp({ to, suffix = '', duration = 1400 }) {
  const ref = useRef(null);
  const startedRef = useRef(false);
  const [val, setVal] = useState(0);

  useEffect(() => {
    const node = ref.current;
    const reduced =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
    if (!node || reduced || typeof IntersectionObserver === 'undefined') {
      setVal(to);
      return;
    }
    let raf;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting || startedRef.current) return;
        startedRef.current = true;
        observer.disconnect();
        const t0 = performance.now();
        const tick = (now) => {
          const p = Math.min((now - t0) / duration, 1);
          const eased = 1 - Math.pow(1 - p, 3);
          setVal(Math.round(eased * to));
          if (p < 1) raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
      },
      { threshold: 0.4 }
    );
    observer.observe(node);
    return () => {
      observer.disconnect();
      if (raf) cancelAnimationFrame(raf);
    };
  }, [to, duration]);

  return (
    <span ref={ref}>
      {val}
      {suffix}
    </span>
  );
}

/* ═══════════════════════════════════════════════════════════════ */
/*  AGENT RUN — workflow rail + live screen of the working agent    */
/* ═══════════════════════════════════════════════════════════════ */

const STAGES = [
  {
    agent: 'JD Voice Agent',
    role: 'Voice → job description',
    icon: FileEdit,
    tint: 'bg-blue-500/10 border-blue-500/30 text-blue-600',
    result: 'JD ready · screening questions built',
    Screen: JDScreen,
  },
  {
    agent: 'Auto-Sourcing Agent',
    role: 'Find & invite matching candidates',
    icon: Globe,
    tint: 'bg-teal-500/10 border-teal-500/30 text-teal-600',
    result: '47 sourced · 142 applied',
    Screen: SourcingScreen,
  },
  {
    agent: 'Screening Agent',
    role: 'AI resume screening',
    icon: BrainCircuit,
    tint: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-600',
    result: '94 passed · ranked by fit',
    Screen: ScreeningScreen,
  },
  {
    agent: 'WhatsApp Agent',
    role: 'Chat screening — CTC, notice & interest',
    icon: MessageCircle,
    tint: 'bg-green-500/10 border-green-500/30 text-green-600',
    result: 'Interest, CTC & notice captured',
    Screen: WhatsAppScreen,
  },
  {
    agent: 'Phone Screen Agent',
    role: 'AI voice call — JD role questions',
    icon: PhoneCall,
    tint: 'bg-indigo-500/10 border-indigo-500/30 text-indigo-600',
    result: '5 JD role questions checked',
    Screen: PhoneScreen,
  },
  {
    agent: 'Scheduler Agent',
    role: 'Auto-scheduling interviews',
    icon: CalendarClock,
    tint: 'bg-violet-500/10 border-violet-500/30 text-violet-600',
    result: 'AI interviews booked 24/7',
    Screen: SchedulerScreen,
  },
  {
    agent: 'Interview Agent',
    role: 'Realtime AI video interviews',
    icon: Video,
    tint: 'bg-cyan-500/10 border-cyan-500/30 text-cyan-600',
    result: '28 cleared · transcripts ready',
    Screen: InterviewScreen,
  },
  {
    agent: 'Coding Agent',
    role: 'Proctored coding tests',
    icon: Code2,
    tint: 'bg-amber-500/10 border-amber-500/30 text-amber-600',
    result: '8 passed · 1 flagged',
    Screen: CodingScreen,
  },
  {
    agent: 'Analytics Agent',
    role: 'Report & shortlist',
    icon: BarChart3,
    tint: 'bg-pink-500/10 border-pink-500/30 text-pink-600',
    result: 'Shortlist ready · 3 booked',
    Screen: AnalyticsScreen,
  },
];

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduced(mq.matches);
    const onChange = (e) => setReduced(e.matches);
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, []);
  return reduced;
}

function AgentRun() {
  // `step` = index of the agent currently working. step === length
  // means the run is complete (hold on the report, then loop).
  const [step, setStep] = useState(0);
  const reduced = usePrefersReducedMotion();

  // Runs continuously and never pauses on hover — only reduced-motion
  // stops it (a static snapshot).
  useEffect(() => {
    if (reduced) return;
    const isComplete = step >= STAGES.length;
    const t = setTimeout(
      () => setStep(isComplete ? 0 : step + 1),
      isComplete ? HOLD_MS : WORK_MS
    );
    return () => clearTimeout(t);
  }, [step, reduced]);

  const effectiveStep = reduced ? 4 : step;
  const complete = effectiveStep >= STAGES.length;
  const activeIdx = Math.min(effectiveStep, STAGES.length - 1);
  const stage = STAGES[activeIdx];
  const ActiveScreen = stage.Screen;
  const Icon = stage.icon;

  // Fraction of the trail the flow has reached (0 → 1), used for the
  // connector fill width and the travelling pulse position.
  const frac = activeIdx / (STAGES.length - 1);

  return (
    <div className="relative bg-card border border-border rounded-2xl shadow-2xl ring-1 ring-foreground/5 overflow-hidden">
      {/* ── Window chrome ── */}
      <div className="flex items-center gap-1.5 px-4 py-3 border-b border-border bg-muted/30">
        <div className="w-2.5 h-2.5 rounded-full bg-red-400/70" />
        <div className="w-2.5 h-2.5 rounded-full bg-yellow-400/70" />
        <div className="w-2.5 h-2.5 rounded-full bg-green-400/70" />
        <div className="ml-3 px-2.5 py-1 rounded-md bg-background/80 border border-border/50 text-[10px] text-muted-foreground font-mono flex-1 max-w-[240px] truncate">
          searchjob.ai / agents / run
        </div>
        <span
          className={`ml-auto inline-flex items-center gap-1.5 text-[10px] font-semibold whitespace-nowrap ${
            complete ? 'text-green-700' : 'text-muted-foreground'
          }`}
        >
          <span className="relative flex w-1.5 h-1.5">
            <span
              className={`absolute inline-flex h-full w-full rounded-full opacity-60 ${
                complete ? 'bg-green-500' : 'bg-primary animate-ping'
              }`}
            />
            <span
              className={`relative inline-flex rounded-full h-1.5 w-1.5 ${
                complete ? 'bg-green-500' : 'bg-primary'
              }`}
            />
          </span>
          {complete ? 'Run complete' : 'Live agent run'}
        </span>
      </div>

      <div className="bg-gradient-to-b from-background to-muted/20">
        {/* ── Flow trail — nodes connected by a filling line ── */}
        <div className="px-4 sm:px-7 pt-6 pb-2">
          <div className="relative flex items-center justify-between">
            {/* base line */}
            <div className="absolute left-[15px] right-[15px] top-[15px] h-0.5 bg-border rounded-full" />
            {/* fill line (grows to the active node) */}
            <div
              className="absolute left-[15px] top-[15px] h-0.5 rounded-full bg-gradient-to-r from-primary via-emerald-500 to-teal-500 transition-all duration-700 ease-out"
              style={{ width: `calc((100% - 30px) * ${frac})` }}
            />
            {/* travelling pulse at the flow head */}
            <span
              className="absolute top-[15px] w-2 h-2 rounded-full bg-primary -translate-x-1/2 -translate-y-1/2 transition-all duration-700 ease-out"
              style={{
                left: `calc(15px + (100% - 30px) * ${frac})`,
                boxShadow: '0 0 10px 2px hsl(var(--primary) / 0.5)',
              }}
            />

            {/* nodes */}
            {STAGES.map((s, idx) => {
              const NIcon = s.icon;
              const done = idx < activeIdx || complete;
              const isActive = idx === activeIdx && !complete;
              return (
                <button
                  key={s.agent}
                  onClick={() => setStep(idx)}
                  aria-label={s.agent}
                  title={s.agent}
                  className="relative z-10 flex items-center justify-center"
                >
                  <span
                    className={`relative w-[30px] h-[30px] rounded-full border-2 flex items-center justify-center bg-card transition-all duration-300 ${
                      done
                        ? 'border-green-500 text-green-600'
                        : isActive
                        ? `${s.tint} scale-[1.18] shadow-md`
                        : 'border-border text-muted-foreground/40'
                    }`}
                  >
                    {done ? (
                      <CheckCircle2 className="w-4 h-4" />
                    ) : (
                      <NIcon className="w-3.5 h-3.5" />
                    )}
                    {isActive && (
                      <span className="absolute -inset-1 rounded-full border-2 border-primary/40 animate-ping" />
                    )}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* ── Spotlight — the active agent, animating in each time ── */}
        <div className="px-4 sm:px-6 pb-5">
          <div key={activeIdx}>
            {/* Identity header */}
            <div className="flex items-center gap-3 mb-3 animate-agent-rise">
              <span
                className={`w-12 h-12 rounded-2xl border flex items-center justify-center shrink-0 ${stage.tint}`}
              >
                <Icon className="w-6 h-6" />
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="text-base sm:text-lg font-bold text-foreground">
                    {stage.agent}
                  </h3>
                  {complete ? (
                    <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-green-50 text-green-700 border border-green-200">
                      <CheckCircle2 className="w-3 h-3" />
                      Run complete
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20">
                      <span
                        className="flex items-end gap-0.5"
                        aria-hidden="true"
                      >
                        <span className="typing-dot" />
                        <span
                          className="typing-dot"
                          style={{ animationDelay: '0.15s' }}
                        />
                        <span
                          className="typing-dot"
                          style={{ animationDelay: '0.3s' }}
                        />
                      </span>
                      Working
                    </span>
                  )}
                </div>
                <p className="text-xs text-muted-foreground truncate">
                  {stage.role}
                </p>
              </div>
              <div className="hidden sm:block text-right shrink-0">
                <div className="text-[9px] uppercase tracking-wider text-muted-foreground font-semibold">
                  Step
                </div>
                <div className="text-sm font-bold text-foreground tabular-nums">
                  {activeIdx + 1}
                  <span className="text-muted-foreground font-normal">
                    /{STAGES.length}
                  </span>
                </div>
              </div>
            </div>

            {/* Live screen of the active agent */}
            <div className="relative h-[260px] sm:h-[300px] rounded-xl border border-border bg-card overflow-hidden animate-screen-rise">
              <div className="absolute inset-0">
                <ActiveScreen complete={complete} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════ */
/*  AGENT SCREENS — what each agent's live work looks like          */
/* ═══════════════════════════════════════════════════════════════ */

/* 1 — JD Agent: voice in, JD out */
function JDScreen() {
  return (
    <div className="h-full p-3.5 flex flex-col bg-gradient-to-b from-background to-muted/20">
      <div className="flex items-center gap-2.5 px-3 py-2 rounded-lg bg-card border border-border shadow-sm mb-3">
        <span className="w-7 h-7 rounded-full bg-primary flex items-center justify-center shrink-0">
          <Mic2 className="w-3.5 h-3.5 text-primary-foreground" />
        </span>
        <span className="text-[11px] sm:text-xs text-foreground/85 italic truncate">
          &ldquo;Hire a senior React developer, four plus years,
          Bengaluru…&rdquo;
        </span>
        <span className="ml-auto flex items-end gap-0.5 shrink-0">
          {[4, 8, 5, 9, 6].map((h, i) => (
            <span
              key={i}
              className="w-0.5 bg-primary rounded-full animate-pulse"
              style={{ height: `${h * 2}px`, animationDelay: `${i * 0.12}s` }}
            />
          ))}
        </span>
      </div>

      <div className="flex-1 rounded-lg bg-card border border-border shadow-sm p-3.5 overflow-hidden">
        <div className="flex items-center justify-between mb-2.5">
          <span className="text-sm font-bold text-foreground">
            Senior Frontend Engineer
          </span>
          <span className="text-[10px] font-bold text-primary inline-flex items-center gap-1 animate-pulse">
            <Sparkles className="w-3 h-3" />
            AI writing…
          </span>
        </div>
        <div className="space-y-1.5">
          {[
            '4+ years building production React applications',
            'TypeScript, Redux and modern testing',
            'Compensation band suggested for Bengaluru',
            '5 screening questions generated',
          ].map((line, i) => (
            <div
              key={line}
              className="flex items-center gap-2 text-[11px] sm:text-xs text-muted-foreground animate-row-in"
              style={{ animationDelay: `${0.25 + i * 0.35}s` }}
            >
              <CheckCircle2 className="w-3 h-3 text-primary shrink-0" />
              {line}
            </div>
          ))}
        </div>
        <div className="flex gap-1.5 mt-3.5">
          {['Voice input', 'ATS-ready'].map((chip) => (
            <span
              key={chip}
              className="text-[9px] font-semibold px-2 py-0.5 rounded-full bg-muted text-foreground/70 border border-border"
            >
              {chip}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

/* 2 — Auto-Sourcing Agent: AI proactively finds & invites candidates,
   alongside inbound applicants from the career page. */
function SourcingScreen() {
  const sourced = [
    { name: 'Priya S.', match: 94, status: 'invited' },
    { name: 'Rahul V.', match: 89, status: 'invited' },
    { name: 'Anjali D.', match: 85, status: 'sourcing' },
  ];
  return (
    <div className="h-full p-3.5 flex flex-col bg-gradient-to-b from-background to-muted/20">
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs sm:text-sm font-semibold text-foreground">
          Auto-sourcing candidates…
        </div>
        <span className="text-[10px] px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20 font-semibold tabular-nums">
          47 sourced · 142 applied
        </span>
      </div>

      {/* Two live inbound channels */}
      <div className="grid grid-cols-2 gap-2 mb-2.5">
        <div className="rounded-lg border border-border bg-card px-2.5 py-1.5">
          <div className="flex items-center gap-1 text-[8px] uppercase tracking-wider text-muted-foreground font-bold mb-0.5">
            <Globe className="w-2.5 h-2.5" />
            Career page
          </div>
          <div className="text-[11px] font-bold text-foreground">
            142 <span className="font-medium text-muted-foreground">applied</span>
          </div>
        </div>
        <div className="rounded-lg border border-primary/30 bg-primary/5 px-2.5 py-1.5">
          <div className="flex items-center gap-1 text-[8px] uppercase tracking-wider text-primary font-bold mb-0.5">
            <Sparkles className="w-2.5 h-2.5" />
            AI-sourced
          </div>
          <div className="text-[11px] font-bold text-foreground">
            47 <span className="font-medium text-muted-foreground">matched</span>
          </div>
        </div>
      </div>

      {/* Live auto-sourcing updates feed */}
      <div className="text-[8px] uppercase tracking-wider text-muted-foreground font-bold mb-1">
        Auto-sourcing updates
      </div>
      <div className="flex-1 space-y-1.5 overflow-hidden">
        {sourced.map((r, i) => (
          <div
            key={r.name}
            className="flex items-center gap-2.5 rounded-lg border border-border bg-card/70 px-2.5 py-1.5 animate-row-in"
            style={{ animationDelay: `${0.2 + i * 0.4}s` }}
          >
            <span className="w-6 h-6 rounded-full bg-primary/10 text-primary text-[9px] font-bold flex items-center justify-center shrink-0">
              {r.name.charAt(0)}
            </span>
            <span className="text-[11px] sm:text-xs font-semibold text-foreground truncate">
              {r.name}
            </span>
            <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-green-50 text-green-700 border border-green-200 shrink-0">
              {r.match}% match
            </span>
            {r.status === 'invited' ? (
              <span className="inline-flex items-center gap-1 text-[9px] font-semibold text-green-700 shrink-0">
                <CheckCircle2 className="w-3 h-3" />
                Invited
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-[9px] font-semibold text-primary shrink-0">
                <span className="flex items-end gap-0.5" aria-hidden="true">
                  <span className="typing-dot" />
                  <span className="typing-dot" style={{ animationDelay: '0.15s' }} />
                  <span className="typing-dot" style={{ animationDelay: '0.3s' }} />
                </span>
                Sourcing
              </span>
            )}
          </div>
        ))}
      </div>

      <div className="pt-2 text-[10px] text-muted-foreground text-center">
        AI matches candidates to the JD and invites the best ones — 24/7
      </div>
    </div>
  );
}

/* 3 — Screening Agent: resumes being scored */
function ScreeningScreen() {
  const rows = [
    { name: 'Priya S.', score: 92, tone: 'green' },
    { name: 'Rahul V.', score: 88, tone: 'green' },
    { name: 'Anjali D.', score: 81, tone: 'amber' },
  ];
  return (
    <div className="h-full p-3.5 flex flex-col bg-gradient-to-b from-background to-muted/20">
      <div className="flex items-center justify-between mb-2.5">
        <div className="text-xs sm:text-sm font-semibold text-foreground">
          Scoring 142 resumes against the JD
        </div>
        <span className="text-[10px] font-bold text-primary inline-flex items-center gap-1 animate-pulse">
          <Sparkles className="w-3 h-3" />
          8 dimensions
        </span>
      </div>

      <div className="flex-1 space-y-2 overflow-hidden">
        {rows.map((r, i) => (
          <div
            key={r.name}
            className="rounded-lg border border-border bg-card px-3 py-2 animate-row-in"
            style={{ animationDelay: `${0.15 + i * 0.35}s` }}
          >
            <div className="flex items-center gap-2 mb-1">
              <span className="w-6 h-6 rounded-full bg-primary/10 text-primary text-[9px] font-bold flex items-center justify-center shrink-0">
                {r.name.charAt(0)}
              </span>
              <span className="text-[11px] sm:text-xs font-semibold text-foreground truncate">
                {r.name}
              </span>
              <span
                className={`ml-auto text-[10px] font-bold px-1.5 py-0.5 rounded border ${
                  r.tone === 'green'
                    ? 'bg-green-50 text-green-700 border-green-200'
                    : 'bg-amber-50 text-amber-700 border-amber-200'
                }`}
              >
                {r.score}
              </span>
            </div>
            <div className="h-1 bg-muted rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full animate-score-fill ${
                  r.tone === 'green' ? 'bg-primary' : 'bg-amber-500'
                }`}
                style={{
                  '--score-w': `${r.score}%`,
                  animationDelay: `${0.3 + i * 0.35}s`,
                }}
              />
            </div>
          </div>
        ))}
      </div>

      <div className="pt-2 text-[10px] text-muted-foreground text-center">
        <span className="font-bold text-primary">94 passed</span> · routed to
        assessments automatically
      </div>
    </div>
  );
}

/* Phone Screen Agent: AI voice call asking 3–5 JD role questions
   (verbal screen, English). Runs AFTER the WhatsApp HR screen. */
function PhoneScreen() {
  const questions = [
    { q: 'Explain how React reconciliation works', status: 'done', score: 9 },
    { q: 'How do you cut unnecessary re-renders?', status: 'done', score: 8 },
    { q: 'Walk me through a state-management tradeoff', status: 'current' },
    { q: 'A production incident you owned', status: 'queued' },
    { q: 'Your testing strategy at scale', status: 'queued' },
  ];
  return (
    <div className="h-full flex flex-col bg-gradient-to-b from-background to-muted/20">
      {/* Call header */}
      <div className="flex items-center justify-between px-3.5 py-2 border-b border-border">
        <div className="flex items-center gap-2 min-w-0">
          <span className="w-7 h-7 rounded-full bg-indigo-500/10 border border-indigo-500/30 text-indigo-600 flex items-center justify-center shrink-0">
            <PhoneCall className="w-3.5 h-3.5" />
          </span>
          <span className="text-[11px] font-semibold text-foreground truncate">
            AI Phone Screen · Priya S.
          </span>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <span className="text-[10px] text-muted-foreground">Q 3 of 5</span>
          <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-red-500/15 text-red-600 font-semibold">
            <span className="w-1 h-1 rounded-full bg-red-500 animate-pulse" />
            REC
          </span>
        </div>
      </div>

      {/* AI asking + waveform */}
      <div className="px-3.5 py-2 flex items-center gap-2.5 border-b border-border/60">
        <span className="relative w-9 h-9 rounded-full bg-indigo-500 flex items-center justify-center shadow shrink-0">
          <Sparkles className="w-4 h-4 text-white" />
          <span className="absolute -inset-1 rounded-full border-2 border-indigo-400/40 animate-ping" />
        </span>
        <div className="flex-1 min-w-0">
          <div className="text-[9px] uppercase tracking-wider text-muted-foreground font-semibold">
            Asking role questions from the JD
          </div>
          <div className="flex items-end gap-0.5 h-3.5 mt-0.5">
            {[5, 9, 6, 11, 7, 10, 6, 8, 5, 9, 7, 11].map((h, i) => (
              <span
                key={i}
                className="flex-1 bg-indigo-400 rounded-full animate-pulse"
                style={{ height: `${h}px`, animationDelay: `${i * 0.08}s` }}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Role-question checklist */}
      <div className="flex-1 p-3 space-y-1 overflow-hidden">
        {questions.map((item, i) => (
          <div
            key={i}
            className={`flex items-center gap-2 rounded-lg border px-2.5 py-1.5 ${
              item.status === 'current'
                ? 'bg-primary/5 border-primary/30'
                : item.status === 'done'
                ? 'bg-card border-border'
                : 'bg-card/50 border-border/60 opacity-55'
            }`}
          >
            {item.status === 'done' ? (
              <CheckCircle2 className="w-3.5 h-3.5 text-green-600 shrink-0" />
            ) : item.status === 'current' ? (
              <span className="flex items-end gap-0.5 shrink-0" aria-hidden="true">
                <span className="typing-dot" />
                <span className="typing-dot" style={{ animationDelay: '0.15s' }} />
                <span className="typing-dot" style={{ animationDelay: '0.3s' }} />
              </span>
            ) : (
              <Clock className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
            )}
            <span className="text-[10px] sm:text-[11px] text-foreground/85 flex-1 truncate">
              {item.q}
            </span>
            {item.status === 'done' && (
              <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-green-50 text-green-700 border border-green-200 shrink-0">
                {item.score}/10
              </span>
            )}
          </div>
        ))}
      </div>

      {/* Live transcript */}
      <div className="px-3.5 py-2 border-t border-border bg-card/60">
        <div className="text-[9px] uppercase tracking-wider text-muted-foreground font-semibold mb-0.5">
          Live transcript · English
        </div>
        <div className="text-[11px] text-foreground/80 italic truncate">
          &ldquo;Walk me through a state-management tradeoff you made
          recently…&rdquo;
        </div>
      </div>
    </div>
  );
}

/* WhatsApp Agent: realtime chat that screens the HR/logistics
   questions — interest, current & expected CTC, notice period — and
   captures them as structured data. Runs BEFORE the phone screen. */
function WhatsAppScreen() {
  return (
    <div className="h-full flex flex-col" style={{ backgroundColor: '#0b141a' }}>
      {/* WhatsApp-style header */}
      <div
        className="flex items-center gap-2.5 px-3.5 py-2"
        style={{ backgroundColor: '#1f2c34' }}
      >
        <span className="w-8 h-8 rounded-full bg-green-500/20 text-green-400 flex items-center justify-center font-bold text-[11px] shrink-0">
          P
        </span>
        <div className="min-w-0">
          <div className="text-[11px] font-semibold text-white truncate">
            Priya S.
          </div>
          <div className="text-[9px] text-green-400">
            online · SearchJob.ai screening
          </div>
        </div>
        <span className="ml-auto inline-flex items-center gap-1 text-[9px] text-white/50 shrink-0">
          <MessageCircle className="w-3 h-3" />
          WhatsApp
        </span>
      </div>

      {/* Chat thread — HR/logistics screen */}
      <div className="flex-1 p-3 space-y-1.5 overflow-hidden">
        <Bubble side="in" delay={0.15}>
          Hi Priya! 👋 Quick screen for the Senior Frontend role. Still
          interested?
        </Bubble>
        <Bubble side="out" delay={0.55}>
          Yes, very! 🙌
        </Bubble>
        <Bubble side="in" delay={0.95}>
          Great — your current &amp; expected CTC, and notice period?
        </Bubble>
        <Bubble side="out" delay={1.4}>
          ₹4.8 LPA → ₹10 LPA · 30 days
        </Bubble>

        {/* Captured structured data */}
        <div
          className="rounded-lg px-3 py-2 animate-row-in"
          style={{ backgroundColor: '#182a23', animationDelay: '1.9s' }}
        >
          <div className="text-[8px] uppercase tracking-wider text-green-300/80 font-bold mb-1">
            Captured automatically
          </div>
          <div className="flex flex-wrap gap-1">
            {['Interested ✓', 'Current ₹4.8L', 'Expected ₹10L', 'Notice 30d'].map(
              (c) => (
                <span
                  key={c}
                  className="text-[9px] text-green-200 px-1.5 py-0.5 rounded-full"
                  style={{ backgroundColor: '#0b3a2e' }}
                >
                  {c}
                </span>
              )
            )}
          </div>
        </div>

        {/* Bot typing the next question */}
        <div className="flex justify-start">
          <span
            className="inline-flex items-center gap-1 rounded-2xl px-3 py-2"
            style={{ backgroundColor: '#202c33' }}
          >
            <span className="wa-dot" />
            <span className="wa-dot" style={{ animationDelay: '0.15s' }} />
            <span className="wa-dot" style={{ animationDelay: '0.3s' }} />
          </span>
        </div>
      </div>

      {/* Footer */}
      <div
        className="px-3.5 py-2 text-[9px] text-white/50"
        style={{ backgroundColor: '#1f2c34' }}
      >
        Realtime chat screening · CTC, notice &amp; interest captured
      </div>
    </div>
  );
}

/* WhatsApp chat bubble — in (bot, left) / out (candidate, right) */
function Bubble({ side, delay, children }) {
  const isOut = side === 'out';
  return (
    <div className={`flex ${isOut ? 'justify-end' : 'justify-start'}`}>
      <span
        className="max-w-[78%] rounded-2xl px-3 py-1.5 text-[11px] leading-snug text-white animate-row-in"
        style={{
          backgroundColor: isOut ? '#005c4b' : '#202c33',
          animationDelay: `${delay}s`,
        }}
      >
        {children}
      </span>
    </div>
  );
}

/* 4 — Scheduler Agent: slots being booked */
function SchedulerScreen() {
  const slots = [
    { name: 'Priya S.', time: 'Today · 3:00 pm', booked: true },
    { name: 'Rahul V.', time: 'Today · 4:30 pm', booked: true },
    { name: 'Anjali D.', time: 'Tomorrow · 11:00 am', booked: true },
    { name: 'Vikram S.', time: 'Tomorrow · 2:00 pm', booked: false },
  ];
  return (
    <div className="h-full p-3.5 flex flex-col bg-gradient-to-b from-background to-muted/20">
      <div className="flex items-center justify-between mb-2.5">
        <div className="text-xs sm:text-sm font-semibold text-foreground">
          Booking AI interview slots
        </div>
        <span className="text-[10px] px-2 py-0.5 rounded-full bg-green-50 text-green-700 border border-green-200 font-semibold">
          No recruiter time used
        </span>
      </div>

      <div className="flex-1 space-y-1.5 overflow-hidden">
        {slots.map((s, i) => (
          <div
            key={s.name}
            className="flex items-center gap-2.5 rounded-lg border border-border bg-card px-2.5 py-2 animate-row-in"
            style={{ animationDelay: `${0.2 + i * 0.35}s` }}
          >
            <span className="w-7 h-7 rounded-lg bg-violet-500/10 border border-violet-500/30 text-violet-600 flex items-center justify-center shrink-0">
              <CalendarClock className="w-3.5 h-3.5" />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block text-[11px] sm:text-xs font-semibold text-foreground truncate">
                {s.name} — AI video interview
              </span>
              <span className="block text-[10px] text-muted-foreground truncate">
                {s.time}
              </span>
            </span>
            {s.booked ? (
              <span className="inline-flex items-center gap-1 text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-green-50 text-green-700 border border-green-200 whitespace-nowrap">
                <CheckCircle2 className="w-2.5 h-2.5" />
                Booked
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20 whitespace-nowrap animate-pulse">
                Inviting…
              </span>
            )}
          </div>
        ))}
      </div>

      <div className="pt-2 text-[10px] text-muted-foreground text-center">
        Reminders sent automatically · candidates pick any time, 24/7
      </div>
    </div>
  );
}

/* 5 — Interview Agent: realtime AI video call */
function InterviewScreen() {
  return (
    <div className="h-full flex flex-col bg-zinc-900">
      <div className="flex items-center justify-between px-3.5 py-2 bg-zinc-950/90 border-b border-zinc-800">
        <span className="text-[11px] font-semibold text-white">
          Round 1 · Priya S.
        </span>
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-white/50">Q 7 of 15</span>
          <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-red-500/15 text-red-400 font-semibold">
            <span className="w-1 h-1 rounded-full bg-red-400 animate-pulse" />
            REC 12:34
          </span>
        </div>
      </div>

      <div className="flex-1 grid grid-cols-2 gap-2 p-3 min-h-0">
        {/* AI interviewer — AI avatar, no real photo */}
        <div className="relative rounded-lg bg-gradient-to-br from-emerald-500/25 via-emerald-600/15 to-teal-600/20 border border-emerald-400/20 flex items-center justify-center overflow-hidden">
          <div className="w-10 h-10 rounded-full bg-emerald-400 flex items-center justify-center shadow-2xl">
            <Sparkles className="w-5 h-5 text-emerald-950" />
          </div>
          <span className="absolute bottom-1.5 left-1.5 text-[8px] font-bold uppercase tracking-wider text-emerald-100 bg-emerald-950/70 px-1.5 py-0.5 rounded">
            AI Interviewer
          </span>
          <span className="absolute top-1.5 right-1.5 flex items-end gap-0.5">
            {[4, 7, 5, 8, 6].map((h, i) => (
              <span
                key={i}
                className="w-0.5 bg-emerald-300/90 rounded-full animate-pulse"
                style={{ height: `${h}px`, animationDelay: `${i * 0.12}s` }}
              />
            ))}
          </span>
        </div>

        {/* Candidate — real candidate photo (public/interviewer.png) */}
        <div className="relative rounded-lg bg-zinc-800 border border-zinc-700 overflow-hidden">
          <img
            src="/interviewer.png"
            alt="Candidate"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-transparent" />
          <span className="absolute bottom-1.5 left-1.5 inline-flex items-center gap-1 text-[8px] font-bold uppercase tracking-wider text-white/90 bg-black/50 px-1.5 py-0.5 rounded">
            <Mic className="w-2 h-2" />
            Priya S.
          </span>
        </div>
      </div>

      <div className="px-3.5 py-2 bg-zinc-950 border-t border-zinc-800">
        <div className="text-[8px] uppercase tracking-wider text-white/40 font-bold mb-0.5">
          Live transcript · Hindi, Tamil, Telugu &amp; English
        </div>
        <div className="text-[10px] sm:text-[11px] text-white/90 italic truncate">
          &ldquo;I refactored the state to a WeakMap so the garbage collector
          could clean up…&rdquo;
        </div>
      </div>
    </div>
  );
}

/* 6 — Coding Agent: proctored test auto-grading */
function CodingScreen() {
  return (
    <div className="h-full flex flex-col bg-zinc-950 font-mono">
      <div className="flex items-center gap-2 px-3.5 py-2 border-b border-zinc-800">
        <div className="w-2 h-2 rounded-full bg-red-400/70" />
        <div className="w-2 h-2 rounded-full bg-yellow-400/70" />
        <div className="w-2 h-2 rounded-full bg-green-400/70" />
        <span className="ml-1 text-[10px] text-zinc-400">solution.py</span>
        <span className="ml-auto inline-flex items-center gap-1 text-[10px] text-green-400 font-sans">
          <span className="w-1 h-1 rounded-full bg-green-400 animate-pulse" />
          Proctoring on
        </span>
      </div>

      <div className="flex-1 p-3.5 text-[10px] sm:text-[11px] space-y-1 text-zinc-300 min-h-0">
        <div>
          <span className="text-purple-400">def</span>{' '}
          <span className="text-blue-400">two_sum</span>(
          <span className="text-orange-400">nums, target</span>):
        </div>
        <div className="pl-4 text-zinc-500"># hash map → O(n) single pass</div>
        <div className="pl-4">seen = {'{}'}</div>
        <div className="pl-4">
          <span className="text-purple-400">for</span> i, num{' '}
          <span className="text-purple-400">in</span>{' '}
          <span className="text-blue-400">enumerate</span>(nums):
        </div>
        <div className="pl-8">
          <span className="text-purple-400">if</span> target - num{' '}
          <span className="text-purple-400">in</span> seen:
        </div>
        <div className="pl-12">
          <span className="text-purple-400">return</span> [seen[target - num],
          i]
        </div>
        <div className="pl-8">seen[num] = i</div>
      </div>

      <div className="px-3.5 py-2 border-t border-zinc-800 flex items-center justify-between font-sans">
        <div className="flex items-center gap-1.5">
          {[1, 2, 3, 4].map((t) => (
            <span
              key={t}
              className="inline-flex items-center gap-0.5 text-[9px] px-1.5 py-0.5 rounded bg-green-500/10 text-green-400 border border-green-500/30 animate-row-in"
              style={{ animationDelay: `${0.3 + t * 0.3}s` }}
            >
              <CheckCircle2 className="w-2.5 h-2.5" />
              T{t}
            </span>
          ))}
        </div>
        <span className="text-[9px] text-emerald-400 font-semibold">
          AI follow-up queued →
        </span>
      </div>
    </div>
  );
}

/* 7 — Analytics Agent: funnel report + shortlist */
function AnalyticsScreen({ complete }) {
  const funnel = [
    { label: 'Applied', count: 142, w: 100 },
    { label: 'AI screened', count: 94, w: 66 },
    { label: 'Interviewed', count: 28, w: 26 },
    { label: 'Assessed', count: 8, w: 12 },
  ];
  return (
    <div className="h-full p-3.5 flex flex-col bg-gradient-to-b from-background to-muted/20">
      <div className="flex items-center justify-between mb-2.5">
        <div className="text-xs sm:text-sm font-semibold text-foreground">
          Hiring report — Senior Frontend Engineer
        </div>
        <span
          className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${
            complete
              ? 'bg-green-50 text-green-700 border-green-200'
              : 'bg-primary/10 text-primary border-primary/20 animate-pulse'
          }`}
        >
          {complete ? 'Report ready' : 'Compiling…'}
        </span>
      </div>

      {/* Funnel bars */}
      <div className="space-y-1.5 mb-3">
        {funnel.map((f, i) => (
          <div key={f.label} className="flex items-center gap-2">
            <span className="w-20 text-[10px] text-muted-foreground text-right shrink-0">
              {f.label}
            </span>
            <div className="flex-1 h-4 bg-muted rounded overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-primary to-emerald-600 rounded animate-score-fill"
                style={{
                  '--score-w': `${f.w}%`,
                  animationDelay: `${0.2 + i * 0.25}s`,
                }}
              />
            </div>
            <span className="w-8 text-[10px] font-bold text-foreground tabular-nums shrink-0">
              {f.count}
            </span>
          </div>
        ))}
      </div>

      {/* Shortlist */}
      <div className="flex-1 rounded-lg border border-primary/30 bg-primary/5 p-2.5 overflow-hidden">
        <div className="text-[10px] font-bold uppercase tracking-wider text-primary mb-1.5">
          Final shortlist · 3 booked
        </div>
        <div className="space-y-1">
          {[
            { n: 'Priya S.', t: 'Tue 3:00 pm' },
            { n: 'Rahul V.', t: 'Wed 11:30 am' },
            { n: 'Anjali D.', t: 'Fri 4:00 pm' },
          ].map((b, i) => (
            <div
              key={b.n}
              className="flex items-center gap-2 animate-row-in"
              style={{ animationDelay: `${0.5 + i * 0.25}s` }}
            >
              <span className="w-5 h-5 rounded-full bg-primary/15 text-primary text-[8px] font-bold flex items-center justify-center shrink-0">
                {b.n.charAt(0)}
              </span>
              <span className="text-[11px] font-semibold text-foreground flex-1 truncate">
                {b.n}
              </span>
              <span className="text-[10px] text-muted-foreground whitespace-nowrap">
                {b.t}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="pt-2 text-[10px] text-muted-foreground text-center">
        Evidence PDFs archived · exportable to CSV / Excel
      </div>
    </div>
  );
}
